import { Link } from "react-router-dom";
import { ArrowRight, ArrowRightLeft } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { CTAPanel } from "../components/CTAPanel";
import { PROJECTS, CASE_STUDIES } from "../data/content";

export default function Portfolio() {
  return (
    <>
      <section className="pt-[72px] hero-grid-bg" data-testid="portfolio-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-16 sm:pt-28 sm:pb-20">
          <Reveal>
            <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">Our work</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-ink max-w-3xl leading-[1.06]">
              Before Avora. <span className="text-gradient">After Avora.</span>
            </h1>
            <p className="mt-6 text-slate text-base sm:text-lg max-w-2xl leading-relaxed">
              Real client websites we've launched, plus sample transformations showing the kind of before-and-after results we deliver for small businesses.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-20" data-testid="client-sites">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-ink mb-3">Client websites, live right now</h2>
            <p className="text-slate text-sm sm:text-base max-w-2xl mb-10">Real businesses we took from zero to fully online — click through and have a look around.</p>
          </Reveal>
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {CASE_STUDIES.map((site) => (
              <StaggerItem key={site.domain}>
                <Link
                  to={`/work/${site.slug}`}
                  data-testid={`client-site-${site.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group block h-full bg-white rounded-[2rem] border border-ink/5 shadow-sm hover:shadow-[0_24px_56px_rgba(20,22,46,0.12)] hover:-translate-y-1.5 transition-[box-shadow,transform] duration-300 overflow-hidden"
                >
                  <div className="flex items-center gap-1.5 px-5 py-3.5 border-b border-ink/5 bg-cloud/70">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                    <span className="ml-3 text-[11px] text-slate bg-white border border-ink/8 rounded-full px-3 py-1 flex items-center gap-1.5">
                      {site.domain}
                      <ArrowRight className="w-3 h-3 text-indigo" />
                    </span>
                  </div>
                  <div className="p-7">
                    <p className="text-xs text-slate mb-1">{site.industry}</p>
                    <h3 className="font-display font-semibold text-ink text-lg mb-2 group-hover:text-indigo transition-colors duration-200">{site.name}</h3>
                    <p className="text-xs text-slate leading-relaxed mb-4">{site.tagline}</p>
                    <div className="flex flex-wrap gap-2">
                      {site.services.map((s) => (
                        <span key={s} className="text-[11px] font-medium text-indigo bg-indigo/8 border border-indigo/15 rounded-full px-3 py-1">{s}</span>
                      ))}
                    </div>
                    <span className="mt-5 inline-flex items-center gap-2 text-indigo font-semibold text-xs group-hover:gap-3 transition-[gap] duration-300">
                      Read the case study <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-24" data-testid="portfolio-grid">
        <div className="max-w-7xl mx-auto mb-10">
          <Reveal>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-ink mb-3">Sample transformations</h2>
            <p className="text-slate text-sm sm:text-base max-w-2xl">Example before-and-after projects showing the kind of results we deliver across industries.</p>
          </Reveal>
        </div>
        <Stagger className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {PROJECTS.map((p) => (
            <StaggerItem key={p.name}>
              <article
                data-testid={`project-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group bg-white rounded-[2rem] border border-ink/5 shadow-sm hover:shadow-[0_24px_56px_rgba(20,22,46,0.1)] hover:-translate-y-1 transition-[box-shadow,transform] duration-300 overflow-hidden"
              >
                <div className="grid grid-cols-2 relative">
                  <div className="bg-cloud p-7 min-h-[170px] flex flex-col justify-center">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate mb-3">Before</p>
                    <p className="text-sm text-slate leading-relaxed">{p.before}</p>
                  </div>
                  <div className="bg-gradient-to-br from-indigo to-mint p-7 min-h-[170px] flex flex-col justify-center">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-white/70 mb-3">After</p>
                    <p className="text-sm text-white font-medium leading-relaxed">{p.after}</p>
                  </div>
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-ink text-white flex items-center justify-center shadow-lg">
                    <ArrowRightLeft className="w-4 h-4" />
                  </span>
                </div>
                <div className="p-7 sm:p-8">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-xs text-slate mb-1">{p.industry}</p>
                      <h2 className="font-display font-semibold text-ink text-lg">{p.name}</h2>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {p.work.map((w) => (
                        <span key={w} className="text-[11px] font-medium text-indigo bg-indigo/8 border border-indigo/15 rounded-full px-3 py-1">{w}</span>
                      ))}
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-ink/70 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-mint mt-1.5 shrink-0" />
                    {p.result}
                  </p>
                </div>
              </article>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal className="max-w-7xl mx-auto mt-14 text-center">
          <p className="text-slate text-sm mb-6">Your business could be the next transformation.</p>
          <Link
            to="/contact"
            data-testid="portfolio-cta"
            className="inline-flex items-center gap-2 bg-ink text-white font-semibold px-8 py-4 rounded-full hover:bg-indigo transition-colors duration-300 group"
          >
            Start your project
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      <CTAPanel />
    </>
  );
}
